import { baseApi } from '@shared/store/api/baseApi';
import type { AgentProfileEditRequestDto, AgentRequestDto } from '@shared/services/apiService';

export type StudioRuntimeSettings = {
  schemaVersion: 1;
  preCall?: { enabled: boolean; url?: string };
  session?: {
    timezone?: string;
    maxDurationSeconds?: number;
    silenceRetries?: number;
    silenceSeconds?: { chat?: number; webcall?: number; telephony?: number };
    styleGuide?: boolean;
  };
  speech?: {
    voicemail?: boolean;
    numberLanguage?: 'auto' | 'eng' | 'arb' | 'deu' | 'rus';
    speed?: number;
    formatting?: boolean;
    redactPii?: boolean;
    background?: 'none' | 'office-ambience';
    unrecognised?: boolean;
    response?: string;
    dictionary?: { word: string; pronunciation: string }[];
    interruptions?: boolean;
    waitTime?: number;
    confidence?: number;
    trigger?: number;
    release?: number;
    smartTurn?: boolean;
    denoising?: boolean;
    waitForUser?: boolean;
    muteUntilGreeting?: boolean;
    volume?: number;
    backoff?: number;
  };
};
export type StudioDraft = {
  // Keep sparse runtime groups explicit across generated native catalog-form versions.
  agent: Omit<AgentRequestDto, 'runtimeSettings'> & {
    runtimeSettings?: StudioRuntimeSettings | null;
  };
  job: { id: string; values: AgentProfileEditRequestDto } | null;
};
export type StudioBranch = {
  id: string;
  name: string;
  draft: StudioDraft;
  baseFingerprint: string;
};
export type StudioDetail = {
  id: string;
  revision: number;
  activeBranchId: string;
  activeVersionId: string | null;
  branches: StudioBranch[];
  native: { draft: StudioDraft; jobType: string | null; fingerprint: string };
  staleBranchIds: string[];
};
export type StudioVersion = {
  id: string;
  number: number;
  branchId: string;
  spec: StudioDraft;
  createdAt: string;
  createdBy: string;
};
type Target = { scope: string; campaignId: string; agentId: string };
type Write = Target & { revision: number; branchId: string };
const url = ({ campaignId, agentId }: Target) => `/v2/workspace/studios/${campaignId}/${agentId}`;
const branchUrl = (target: Write) => `${url(target)}/branches/${target.branchId}`;
const api = baseApi
  .enhanceEndpoints({ addTagTypes: ['AgentStudio', 'Workspace'] })
  .injectEndpoints({
    endpoints: (build) => ({
      agentStudio: build.query<StudioDetail, Target>({
        query: url,
        providesTags: ['AgentStudio'],
      }),
      initializeAgentStudio: build.mutation<StudioDetail, Target>({
        query: (target) => ({ url: url(target), method: 'POST', body: {} }),
        invalidatesTags: ['AgentStudio'],
      }),
      agentStudioVersions: build.query<
        { items: StudioVersion[]; next: string | null },
        Target & { before?: string }
      >({
        query: (target) => ({ url: `${url(target)}/versions`, params: { before: target.before } }),
        providesTags: ['AgentStudio'],
      }),
      createAgentStudioBranch: build.mutation<
        StudioDetail,
        Target & { revision: number; name: string; sourceBranchId: string }
      >({
        query: ({ revision, name, sourceBranchId, ...target }) => ({
          url: `${url(target)}/branches`,
          method: 'POST',
          body: { revision, name, sourceBranchId },
        }),
        invalidatesTags: ['AgentStudio'],
      }),
      saveAgentStudioBranch: build.mutation<StudioDetail, Write & { draft: StudioDraft }>({
        query: ({ revision, draft, ...target }) => ({
          url: branchUrl({ ...target, revision }),
          method: 'PATCH',
          body: { revision, draft },
        }),
        invalidatesTags: ['AgentStudio'],
      }),
      deleteAgentStudioBranch: build.mutation<StudioDetail, Write>({
        query: (target) => ({
          url: branchUrl(target),
          method: 'DELETE',
          body: { revision: target.revision },
        }),
        invalidatesTags: ['AgentStudio'],
      }),
      syncAgentStudioBranch: build.mutation<StudioDetail, Write>({
        query: (target) => ({
          url: `${branchUrl(target)}/sync`,
          method: 'POST',
          body: { revision: target.revision },
        }),
        invalidatesTags: ['AgentStudio'],
      }),
      restoreAgentStudioVersion: build.mutation<StudioDetail, Write & { versionId: string }>({
        query: (target) => ({
          url: `${branchUrl(target)}/restore`,
          method: 'POST',
          body: { revision: target.revision, versionId: target.versionId },
        }),
        invalidatesTags: ['AgentStudio'],
      }),
      publishAgentStudioBranch: build.mutation<
        { studio: StudioDetail; version: StudioVersion },
        Write & { requestId: string }
      >({
        query: (target) => ({
          url: `${branchUrl(target)}/publish`,
          method: 'POST',
          body: { revision: target.revision, requestId: target.requestId },
        }),
        invalidatesTags: ['AgentStudio', 'Campaign', 'Workspace'],
      }),
    }),
  });

export const {
  useAgentStudioQuery,
  useAgentStudioVersionsQuery,
  useInitializeAgentStudioMutation,
  useCreateAgentStudioBranchMutation,
  useSaveAgentStudioBranchMutation,
  useDeleteAgentStudioBranchMutation,
  useSyncAgentStudioBranchMutation,
  useRestoreAgentStudioVersionMutation,
  usePublishAgentStudioBranchMutation,
} = api;
